import {clamp} from '@benchling/js-utils';

import * as analytics from 'analytics/Analytics';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorModal from 'file-browser/FolderSelectorModal';
import {getInsufficientPermissionsToEditMessage} from 'folder-items/FileUtil';
import ProxyFile, {ProxyFileAttributes, ProxyFileSetOptions} from 'folder-items/ProxyFile';
import {IsContainedInComplexPolymerData} from 'modbio/complex-polymers/ComplexPolymerConstants';
import CodonOptimizationTabState from 'molbio/codon-optimization/CodonOptimizationTabState';
import {Selection} from 'molbio/maps/selectables/Selection';
import ProteinActions from 'molbio/proteins/ProteinActions';
import ProteinDAO from 'molbio/proteins/ProteinDAO';
import ProteinStore from 'molbio/proteins/ProteinStore';
import {
  Protein as ProteinType,
  ProteinAnnotationVersion,
  ProteinPart,
} from 'molbio/proteins/util/ProteinConstants';
import * as sequtil from 'molbio/SeqUtil';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {selectRange} from 'toolbar/protein/ProteinUIGlobalState';

interface ProteinAttributes extends ProxyFileAttributes {
  protein: ProteinType;
  selection: Selection;
  id: string;
  // Whether the model is a read-only representation of a protein outside of the usual protein workspace context
  isTransientDisplay?: boolean;
}

interface Protein {
  get<K extends keyof ProteinAttributes>(key: K): ProteinAttributes[K];
  set<K extends keyof ProteinAttributes>(
    attributeName: K,
    value: ProteinAttributes[K],
    options?: ProxyFileSetOptions
  ): Protein;
  set(obj: Partial<ProteinAttributes>, options?: ProxyFileSetOptions): Protein;
}

class Protein extends ProxyFile<ProteinType, typeof ProteinStore> {
  static staticInit(): void {
    this.prototype.proxiedField = 'protein';
  }

  override initialize(): void {
    super.initialize();
    this.set('selection', new Selection({sequence: this}), {bypassProxyBlock: true});
    this.on('change:protein', () => {
      this.clampSelection();
      this.setBanner();
    });
  }

  override usesAutoSave(): boolean {
    return false;
  }

  selection(): Selection {
    return this.get('selection');
  }

  getStore(): typeof ProteinStore {
    return ProteinStore;
  }

  getState(): {protein: ProteinType} {
    return {protein: this.getStore().getProteinById(this.id)!};
  }

  aminoAcids(): string {
    return this.proxied().version.aminoAcids;
  }

  annotations(): Array<ProteinAnnotationVersion> {
    return this.proxied().version.annotations;
  }

  parts(): Array<ProteinPart> {
    return this.proxied().version.parts ?? [];
  }

  length(): number {
    return this.aminoAcids().length;
  }

  fragments(): Array<{
    start: number;
    end: number;
    color: string;
  }> {
    return this.proxied().version.fragments ?? [];
  }

  load({deferred}: {versionId?: number | null; deferred: JQueryDeferred<void>}): void {
    ProteinActions.open(this.id, {deferred, view: true});
  }

  override setTags(tags: ProteinType['tags']): void {
    ProteinActions.postServerUpdate(this.id, {tags});
  }

  override setTagSchema(tagSchema: ProteinType['tagSchema']): void {
    ProteinActions.postServerUpdate(this.id, {tagSchema});
  }

  override setAuthors(authors: ProteinType['authors']): void {
    ProteinActions.postServerUpdate(this.id, {authors});
  }

  override setArchiveRecord(archiveRecord: ProteinType['archiveRecord']): void {
    ProteinActions.postServerUpdate(this.id, {archiveRecord});
  }

  override setSchemaValidationState(schemaValidationState: ProteinType['schemaValidationState']): void {
    ProteinActions.postServerUpdate(this.id, {schemaValidationState});
  }

  override setPermissions(permissions: ProteinType['permissions']): void {
    ProteinActions.postServerUpdate(this.id, {permissions});
  }

  isTransientDisplay(): boolean {
    return this.get('isTransientDisplay') ?? false;
  }

  override isLatest(): boolean {
    const proxied = this.proxied();
    return proxied && proxied.isLatest;
  }

  tagSchemaId(): string | null {
    return this.proxied().tagSchema?.id ?? null;
  }

  override writable(): boolean {
    return this.permissions().writable;
  }

  override allowEdits(): boolean {
    return this.writable() && this.isLatest();
  }

  allowFeatureEdits(): boolean | undefined {
    return this.permissions().polymerFeaturesEditable && this.isLatest() && !this.isTransientDisplay();
  }

  isContainedInComplexPolymer(): boolean {
    const proxied = this.proxied();
    return proxied && (proxied.isContainedInComplexPolymerData?.isContained ?? false);
  }

  isContainedInComplexPolymerData(): IsContainedInComplexPolymerData | null {
    const proxied = this.proxied();
    if (proxied && proxied.isContainedInComplexPolymerData) {
      return proxied.isContainedInComplexPolymerData;
    }
    return null;
  }

  allowResidueEdits(): boolean {
    return (
      this.permissions().polymerResiduesEditable! &&
      this.isLatest() &&
      !this.isContainedInComplexPolymer() &&
      !this.isTransientDisplay()
    );
  }

  isSelectionEditable(): boolean {
    return this.getPartsInSelection(this.selection().start(), this.selection().end()).length === 0;
  }

  canInsertAt(position: number): boolean {
    return this.getPartsInSelection(position, null).length === 0;
  }

  isSelectionDeletable(): boolean {
    const selection = this.selection();
    return selection.isNotEmpty() && this.canInsertAt(selection.start()) && this.canInsertAt(selection.end());
  }

  circular(): boolean {
    return false;
  }

  /**
   * When the protein residues change, clamp the selection state to the ends of the sequence if it is no longer valid.
   */
  clampSelection(): void {
    const clampIndex = (index: number | null): number | null =>
      index == null ? null : clamp(0, this.length(), index);
    const [currentStart, currentEnd] = this.selection().getEndpoints();
    const clampedStart = clampIndex(currentStart);
    const clampedEnd = clampIndex(currentEnd);
    selectRange(this.id, clampedStart, clampedEnd);
  }

  // TODO T3917: refactor notifications and setBanner to use same text
  override setBanner(): void {
    if (!this.isLatest()) {
      this.setBannerText('Editing disabled on previous versions.');
    } else if (!this.allowResidueEdits()) {
      if (this.isArchived()) {
        this.setBannerText('Editing disabled on archived AA sequence.');
      } else if (this.isContainedInComplexPolymer()) {
        this.setBannerText(
          `Editing residues is disabled because AA sequence ${this.isContainedInComplexPolymerData()!.errorMessage!}`
        );
      } else if (this.allowFeatureEdits() || this.writable()) {
        this.setBannerText('Editing residues is disabled due to insufficient permissions.');
      } else {
        this.setBannerText(getInsufficientPermissionsToEditMessage(this.permissions(), 'AA sequence'));
      }
    } else {
      this.setBannerText(null);
    }
  }

  residueSlice(start: number, end: number): string {
    return sequtil.residueSlice(this.aminoAcids(), start, end, false);
  }

  getPartsBetween(start: number, end: number): Array<ProteinPart> {
    return this.parts().filter((part) => sequtil.isRangeOverlap(part.start, part.end, start, end));
  }

  getPartsInSelection(selectionStart: number, selectionEnd: number | null): Array<ProteinPart> {
    if (selectionEnd == null) {
      const position = selectionStart === this.length() ? 0 : selectionStart;
      return this.parts().filter((part) => sequtil.baseInExclusiveRange(position, part.start, part.end));
    } else {
      return this.getPartsBetween(selectionStart, selectionEnd);
    }
  }

  handleDoubleClick(regionStart: number, regionEnd: number): void {
    const index = this.selection().start();
    const partsAtIndex = this.getPartsInSelection(index, null);
    if (index !== this.length() && partsAtIndex.length === 1) {
      const [partToSelect] = partsAtIndex;
      this.selection().setEndpoints(partToSelect.start, partToSelect.end);
    } else {
      // Default behavior is to select the whole "region" (e.g. slice)
      this.selection().reset(regionStart);
      this.selection().setEndpoints(regionStart, regionEnd);
    }
  }

  startIndex(): number {
    return 1;
  }

  isSingleStranded(): boolean {
    return true;
  }

  deleteSelection(): void {
    ProteinActions.deleteAminoAcids(
      this.id,
      {
        start: this.selection().start(),
        end: this.selection().end()!,
      },
      () => {
        selectRange(this.id, this.selection().start(), null);
      }
    );
  }

  override schemaValidationState(): SchemaValidationState | null {
    return this.get('protein').schemaValidationState;
  }

  async handleBackTranslate(): Promise<void> {
    const aaSelection = this.residueSlice(this.selection().start(), this.selection().end()!);
    const reverseTranslation = await ProteinDAO.reverseTranslateSelection(aaSelection);
    const tab = new CodonOptimizationTabState({bases: reverseTranslation.bases, protein: this});
    this.fileState().buffers().addTab(tab);
    analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.OPENED_CODON_OPTIMIZATION, {polymerId: this.get('id')});
  }

  duplicate(): void {
    FolderSelectorModal.openForCopy(
      {
        initialSelectedFolderId: this.folder()!.api_identifier,
        showInventory: true,
        showNotebook: false,
      },
      (folderId) => {
        FileBrowserActions.copyAndOpenItem(this.id, folderId);
      }
    );
  }

  getActiveVersionId(): number {
    return this.get('protein').version.id;
  }
}
Protein.staticInit();

export default Protein;
